import { Button } from '@/shared/ui/button';
import { addDays, format } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import type { StepDirection } from './types';

interface CalendarToolbarProps {
  /** Left edge of the visible window for the active view. */
  rangeStart: Date;
  /** Number of days visible — used for the date-range label. */
  daysToShow: number;
  /** Called by prev/next; the caller decides what the step unit is. */
  onStep: (direction: StepDirection) => void;
  /** Called when Today is clicked. Caller may also reset zoom etc. */
  onToday: () => void;
  /** View-specific control slot (zoom buttons, day-view select, …). */
  viewControls?: ReactNode;
}

export function CalendarToolbar({
  rangeStart,
  daysToShow,
  onStep,
  onToday,
  viewControls,
}: CalendarToolbarProps) {
  const rangeEnd = addDays(rangeStart, Math.max(daysToShow - 1, 0));
  const sameYear = rangeStart.getFullYear() === rangeEnd.getFullYear();
  const label =
    daysToShow <= 1
      ? format(rangeStart, 'MMM d, yyyy')
      : sameYear
        ? `${format(rangeStart, 'MMM d')} – ${format(rangeEnd, 'MMM d, yyyy')}`
        : `${format(rangeStart, 'MMM d, yyyy')} – ${format(rangeEnd, 'MMM d, yyyy')}`;

  return (
    <div className="flex items-center gap-3">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onStep(-1)}
        className="bg-background border-border text-foreground hover:bg-accent"
        aria-label="Previous"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded">
        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-foreground whitespace-nowrap">{label}</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onStep(1)}
        className="bg-background border-border text-foreground hover:bg-accent"
        aria-label="Next"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onToday}
        className="bg-background border-border text-foreground hover:bg-accent"
      >
        Today
      </Button>

      {viewControls}
    </div>
  );
}
